import { useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { joinWaitlist, looksLikeEmail, type WaitlistResult } from "@/lib/personal/waitlist";
import { BTN_PRIMARY, FOCUS } from "./ui";
import { cn } from "@/lib/utils";

type State =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "done" }
  | { kind: "error"; reason: Exclude<WaitlistResult, { ok: true }>["reason"] };

const ERROR_KEY = {
  "invalid-email": "home.waitlist.invalid",
  "rate-limited": "home.waitlist.rateLimited",
  unavailable: "home.waitlist.unavailable",
  failed: "home.waitlist.failed",
} as const;

/**
 * The Photohawk app (Photohawk Personal) waitlist, on the light hero of /personal: the dashboard's field and button. Joining IS the consent, and the
 * consent text sits right under the button: one email when the app is ready, no marketing. Never a sign-up: the
 * app has no web account.
 */
const WaitlistForm = ({ submit = joinWaitlist }: { submit?: typeof joinWaitlist }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });
  const inputRef = useRef<HTMLInputElement>(null);
  const uid = useId();
  const inputId = `waitlist-email-${uid}`;
  const noteId = `waitlist-note-${uid}`;
  const errorId = `waitlist-error-${uid}`;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.kind === "sending") return;
    if (!looksLikeEmail(email)) {
      setState({ kind: "error", reason: "invalid-email" });
      inputRef.current?.focus();
      return;
    }
    setState({ kind: "sending" });
    const res = await submit(email, { locale: i18n.language, source: "web-home" });
    if ("reason" in res) {
      setState({ kind: "error", reason: res.reason });
      if (res.reason === "invalid-email") inputRef.current?.focus();
    } else setState({ kind: "done" });
  };

  if (state.kind === "done") {
    return (
      <div
        id="waitlist"
        role="status"
        data-testid="waitlist-success"
        className="flex items-start gap-3 rounded-xl border border-border bg-accent px-4 py-4 text-base font-semibold text-foreground"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#A6560A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 flex-none">
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        </svg>
        <span>{t("home.waitlist.success")}</span>
      </div>
    );
  }

  const invalid = state.kind === "error" && state.reason === "invalid-email";
  return (
    <form id="waitlist" noValidate onSubmit={onSubmit} aria-label={t("home.waitlist.formLabel")} className="flex flex-col gap-2.5">
      <div className="flex flex-wrap gap-2.5">
        <label htmlFor={inputId} className="sr-only">
          {t("home.waitlist.emailLabel")}
        </label>
        <input
          ref={inputRef}
          id={inputId}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (state.kind === "error") setState({ kind: "idle" });
          }}
          placeholder={t("home.waitlist.placeholder")}
          aria-invalid={invalid || undefined}
          aria-describedby={`${noteId}${state.kind === "error" ? ` ${errorId}` : ""}`}
          className={`h-14 min-w-0 flex-[1_1_240px] rounded-xl border bg-background px-4 text-base text-foreground placeholder:text-muted-foreground ${FOCUS} ${invalid ? "border-[#B42318] shadow-[0_0_0_1px_#B42318]" : "border-muted-foreground"}`}
        />
        <button
          type="submit"
          disabled={state.kind === "sending"}
          className={cn(BTN_PRIMARY, "flex-[1_0_auto] rounded-xl px-6 disabled:cursor-wait disabled:opacity-80 sm:flex-none")}
        >
          {state.kind === "sending" ? t("home.waitlist.sending") : t("home.waitlist.button")}
        </button>
      </div>
      <p id={noteId} className="m-0 text-[13px] leading-normal text-muted-foreground">
        {t("home.waitlist.consent")}
      </p>
      <p id={errorId} role="alert" data-testid="waitlist-error" className="m-0 min-h-0 text-sm font-semibold text-[#B42318] empty:hidden">
        {state.kind === "error" ? t(ERROR_KEY[state.reason]) : ""}
      </p>
    </form>
  );
};

export default WaitlistForm;
